import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { toast } from 'react-hot-toast';

export interface EmojisConfig {
  positivos: string[];
  neutros: string[];
  negativos: string[];
  saudacao: string[];
  fechamento: string[];
  contextuais?: Record<string, string>;
}

/**
 * Hook para gerenciar configuração de emojis
 * ✅ 100% DINÂMICO - Carrega/salva via API
 */
export const useEmojisConfig = (userId: string | null) => {
  const queryClient = useQueryClient();

  const { data: emojis, isLoading, error, dataUpdatedAt } = useQuery<EmojisConfig>({
    queryKey: ['emojis-config', userId],
    queryFn: async () => {
      if (!userId) throw new Error('userId é obrigatório');

      console.log('[useEmojisConfig] Carregando emojis do backend...');
      const response = await api.get(`/api/config/emojis/${userId}`);
      console.log('[useEmojisConfig] ✅ Emojis carregados do backend:', response.data);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    // ✅ Garantir que refetch após reset realmente busque dados novos
    refetchOnMount: true,
  });

  const saveEmojis = useMutation({
    mutationFn: async (config: EmojisConfig) => {
      if (!userId) throw new Error('userId é obrigatório');

      console.log('[useEmojisConfig] Salvando emojis:', config);
      const response = await api.post(`/api/config/emojis/${userId}`, config);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emojis-config', userId] });
      // 🔥 Feedback de sync em tempo real
      toast.success('✅ Salvo! Bot já está usando os novos emojis', {
        duration: 3000,
        icon: '⚡',
      });
    },
    onError: (error: any) => {
      toast.error('❌ Erro ao salvar emojis: ' + (error.response?.data?.message || error.message));
    },
  });

  return {
    emojis: emojis ?? createDefaultEmojis(),
    isLoading,
    error,
    saveEmojis: saveEmojis.mutate,
    isSaving: saveEmojis.isPending,
  };
};

function createDefaultEmojis(): EmojisConfig {
  return {
    positivos: ['😊', '😄', '🥰', '✨', '😂', '👍', '🙏'],
    neutros: ['😌', '🙂', '👍', '🤔', '👉'],
    negativos: ['😔', '😕', '🤗'],
    saudacao: ['👋', '😊', '🙂'],
    fechamento: ['😊', '👋', '💙']
  };
}
